/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { ClientModel } from '@/domain/models/client.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';

type State = { clients: ClientModel[]; isLoading: boolean; error: string | null };

export function useClients(controller: ClientController) {
    const [state, setState] = useState<State>({ clients: [], isLoading: true, error: null });
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true, error: null }));
        const result = await controller.loadClients(search || undefined);
        result.fold(
            (error) => setState({ clients: [], isLoading: false, error: error.message }),
            (clients) => setState({ clients, isLoading: false, error: null })
        );
    }, [controller, search]);

    useEffect(() => {
        load();
    }, [load]);

    const createClient = useCallback(
        async (params: { name: string; abbreviation: string; logoUrl?: string; aiModel?: string }) => {
            const result = await controller.createClient(params);
            if (result.isRight()) {
                await load();
            }
            return result;
        },
        [controller, load]
    );

    const updateClient = useCallback(
        async (id: string, params: { name: string; abbreviation: string; logoUrl?: string; aiModel?: string }) => {
            const result = await controller.updateClient(id, params);
            if (result.isRight()) {
                await load();
            }
            return result;
        },
        [controller, load]
    );

    const uploadLogo = useCallback(
        async (clientId: string, params: { fileData: string; fileName: string; mimeType: string }) => {
            const result = await controller.uploadLogo(clientId, params);
            if (result.isRight()) {
                await load();
            }
            return result;
        },
        [controller, load]
    );

    const removeLogo = useCallback(
        async (clientId: string) => {
            const result = await controller.removeLogo(clientId);
            if (result.isRight()) {
                await load();
            }
            return result;
        },
        [controller, load]
    );

    return { ...state, search, setSearch, reload: load, createClient, updateClient, uploadLogo, removeLogo };
}
